import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, RefreshCw, Save, Trash2 } from 'lucide-react'
import type { RowSelectionState } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  ExportDrawer,
  FullscreenButton,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  Popover,
  PopoverMenu,
  PopoverMenuGroup,
  PopoverMenuItem,
  PopoverTrigger,
  QueryErrorState,
  SearchInput,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
  toast,
  useFullscreen,
} from '@/components/ui'
import { ScopeChip } from '@/shell/components/ScopeChip'
import { useAuth } from '@/shell/module-system'
import { useOpenOrders } from '../api/queries'
import type { OpenOrderRow, OpenOrdersFilter } from '../api/types'
import type { DatePreset } from '@/components/ui'
import { useOpenOrdersColumns } from '../hooks/useOpenOrdersColumns'
import { OpenOrdersFilterRail } from '../components/OpenOrdersFilterRail'
import { OrderDrawer } from '../components/OrderDrawer'
import { OrderHistorySummary } from '../components/OrderHistorySummary'
import { ProductCoverageDrawer } from '../components/ProductCoverageDrawer'
import { COLUMNS as ORDER_EXPORT_COLUMNS } from '../utils/csv'
import { isCriticalOpenOrder } from '../components/StateCell'
import {
  addSaved,
  loadSaved,
  removeSaved,
  type SavedSearch,
} from '../utils/saved-searches'

const TODAY = new Date()

function currency(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

function twelveMonthsAgo(): string {
  const d = new Date(TODAY)
  d.setFullYear(d.getFullYear() - 1)
  return d.toISOString().slice(0, 10)
}

function defaultFilter(): OpenOrdersFilter {
  const yearStart = new Date(TODAY.getFullYear(), 0, 1)
  return {
    date_from: yearStart.toISOString().slice(0, 10),
    date_to: TODAY.toISOString().slice(0, 10),
    rules: { outstanding_only: true },
    exclusions: {},
  }
}

/**
 * 2026-07-21 — Order History preset for outside_sales_rep. Reuses the
 * exact same query shape/table/columns as the default Open view; only
 * the filter values differ (all confirmed orders, not just outstanding
 * ones, from the rep's history floor onward). `outside_sales_rep`'s
 * `sale.order.user_id`-based scoping already guarantees this only ever
 * shows their own assigned orders — nothing new needed here for that.
 */
function historyFilter(minDate?: string): OpenOrdersFilter {
  return {
    // Without a role floor, fall back to 12 months rather than
    // `defaultFilter()`'s Jan-1-this-year — in January that would be a
    // history view holding days of data.
    date_from: minDate ?? twelveMonthsAgo(),
    date_to: TODAY.toISOString().slice(0, 10),
    rules: { outstanding_only: false },
    exclusions: {},
  }
}

type ViewMode = 'table' | 'summary'
type OrdersMode = 'open' | 'history'

export function OpenOrdersPage() {
  const user = useAuth()
  // 2026-09-11 — Order History is available to every role that can reach
  // this page. It shipped 2026-07-21 gated to outside_sales_rep, which
  // left everyone else with no way to look up what a customer has bought.
  // The date floor stays role-driven: `orderHistoryFloor` comes from
  // `rbac.ROLES[role].order_history_floor` and is only set for reps, and
  // the server re-clamps it in `_clamp_date_from` regardless of what the
  // client sends — so ungating the toggle does not widen a rep's window.
  const isRep = user.permissions.has('outside_sales_rep')
  const minDate = user.orderHistoryFloor

  const [filter, setFilter] = useState<OpenOrdersFilter>(defaultFilter)
  const [mode, setMode] = useState<OrdersMode>('open')
  const [preset, setPreset] = useState<DatePreset>('ytd')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(undefined)
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(true)
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(() => loadSaved())
  const { fullscreen, toggle: toggleFullscreen } = useFullscreen()
  const [coverageProductId, setCoverageProductId] = useState<number | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Debounce search by 200ms (spec §11.3)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const columns = useOpenOrdersColumns()
  const { data, isLoading, isFetching, isError, error, refetch } = useOpenOrders(filter)

  const filtered = useMemo<OpenOrderRow[]>(() => {
    const rows = data?.rows ?? []
    if (!debouncedSearch) return rows
    const needle = debouncedSearch.toLowerCase()
    return rows.filter((r) => {
      return (
        r.customer?.toLowerCase().includes(needle) ||
        r.po_number?.toLowerCase().includes(needle) ||
        r.so_number?.toLowerCase().includes(needle) ||
        r.product_name?.toLowerCase().includes(needle) ||
        r.vendor?.toLowerCase().includes(needle) ||
        r.customer_part_number?.toLowerCase().includes(needle)
      )
    })
  }, [data, debouncedSearch])

  const totalRows = data?.row_count ?? 0
  const meta = data?.meta

  const selectedRow = useMemo(() => {
    if (!selectedRowId) return null
    return filtered.find((r) => String(r.sol_id) === selectedRowId) ?? null
  }, [selectedRowId, filtered])

  // Table-relative prev/next for the drawer.
  const currentIndex = selectedRow
    ? filtered.findIndex((r) => r.sol_id === selectedRow.sol_id)
    : -1
  const drawerPrev =
    currentIndex > 0 ? () => setSelectedRowId(String(filtered[currentIndex - 1]!.sol_id)) : undefined
  const drawerNext =
    currentIndex >= 0 && currentIndex < filtered.length - 1
      ? () => setSelectedRowId(String(filtered[currentIndex + 1]!.sol_id))
      : undefined

  // `/` focuses search (principle 6 + DataView acceptance).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  function handleModeChange(next: OrdersMode) {
    setMode(next)
    if (next === 'history') {
      setFilter(historyFilter(minDate))
      setPreset(minDate ? 'custom' : '12mo')
    } else {
      setFilter(defaultFilter())
      setPreset('ytd')
    }
  }

  return (
    <div
      data-density="compact"
      className={
        fullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-canvas'
          : 'flex h-full flex-col'
      }
    >
      <PageHeaderBand
        title={mode === 'history' ? 'Order History' : 'Open Orders'}
        description={
          mode === 'history'
            ? isRep
              ? 'Every confirmed sale order assigned to you, open or closed.'
              : 'Every confirmed sale order line, open or closed. Filter by customer to see what they have bought.'
            : 'Every open SOL with vendor coverage. Critical rows (late + uncovered) accent red.'
        }
      >
        <ScopeChip />
        <ViewToggle
          aria-label="Orders mode"
          value={mode}
          onChange={handleModeChange}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'history', label: 'History' },
          ]}
        />
        {meta && mode === 'history' ? (
          <MetricStrip variant="inline" className="ml-auto">
            <MetricCard variant="inline" label="Lines" value={totalRows.toLocaleString()} />
            <MetricCard
              variant="inline"
              label="Revenue"
              value={currency(data?.totals?.revenue ?? 0)}
            />
            <MetricCard
              variant="inline"
              label="Est. margin"
              value={currency(data?.totals?.est_margin ?? 0)}
            />
            <MetricCard
              variant="inline"
              label="Customers"
              value={meta.customer_count.toLocaleString()}
            />
            <MetricCard
              variant="inline"
              label="Products"
              value={meta.product_count.toLocaleString()}
            />
          </MetricStrip>
        ) : meta ? (
          <MetricStrip variant="inline" className="ml-auto">
            <MetricCard variant="inline" label="Open rows" value={totalRows.toLocaleString()} />
            <MetricCard
              variant="inline"
              label="Late"
              value={meta.late_count.toLocaleString()}
              valueTone={meta.late_count > 0 ? 'danger' : 'default'}
            />
            <MetricCard
              variant="inline"
              label="Customers"
              value={meta.customer_count.toLocaleString()}
            />
            <MetricCard variant="inline" label="Products" value={meta.product_count.toLocaleString()} />
          </MetricStrip>
        ) : null}
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1">
        <OpenOrdersFilterRail
          filter={filter}
          onChange={(next) => setFilter(next)}
          collapsed={filterRailCollapsed}
          onToggleCollapsed={() => setFilterRailCollapsed((v) => !v)}
          preset={preset}
          onPresetChange={setPreset}
          minDate={minDate}
          savedSearchSlot={
            savedSearches.length > 0 ? (
              <ul className="flex flex-col gap-1 text-sm">
                {savedSearches.map((s) => (
                  <li key={s.id} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setFilter(s.filter)}
                      className="min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent p-0 text-left text-link hover:underline"
                    >
                      {s.label}
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${s.label}`}
                      onClick={() => setSavedSearches(removeSaved(s.id))}
                      className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null
          }
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <Toolbar>
            <SearchInput
              ref={searchInputRef}
              containerClassName="flex-1 max-w-none"
              placeholder="Search SO, PO, customer, product…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
          </Toolbar>
          <Toolbar>
            <ToolbarLeft>
              <ViewToggle
                aria-label="View mode"
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { value: 'table', label: 'Table' },
                  { value: 'summary', label: 'Summary' },
                ]}
              />
            </ToolbarLeft>
            <ToolbarRight>
              <ToolbarCount>
                {isError
                  ? '—'
                  : `${filtered.length.toLocaleString()} of ${totalRows.toLocaleString()}`}
              </ToolbarCount>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Refresh"
                loading={isFetching && !isLoading}
                onClick={() => refetch()}
              >
                <RefreshCw size={14} />
              </Button>
              <FullscreenButton fullscreen={fullscreen} onToggle={toggleFullscreen} />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setExportOpen(true)}
                disabled={filtered.length === 0}
                title="Pick columns or apply a saved template before download"
              >
                <Download size={12} /> Export…
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" iconOnly aria-label="More actions">
                    <Save size={14} />
                  </Button>
                </PopoverTrigger>
                <PopoverMenu>
                  <PopoverMenuGroup label="Saved searches">
                    <PopoverMenuItem
                      icon={Save}
                      onSelect={() => {
                        const label = window.prompt(
                          'Name this search',
                          defaultLabelFor(filter),
                        )
                        if (!label) return
                        setSavedSearches(addSaved(label, filter))
                        toast.success(`Saved "${label}"`)
                      }}
                    >
                      Save current filters…
                    </PopoverMenuItem>
                  </PopoverMenuGroup>
                </PopoverMenu>
              </Popover>
            </ToolbarRight>
          </Toolbar>

          {viewMode === 'table' ? (
            <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
              <DataTable
                data={filtered}
                columns={columns}
                loading={isLoading}
                error={isError ? error : undefined}
                onRetry={() => void refetch()}
                errorSubject="open orders"
                criticalRowPredicate={isCriticalOpenOrder}
                selection={selection}
                onSelectionChange={setSelection}
                onRowClick={(r) => setSelectedRowId(String(r.sol_id))}
                getRowId={(r) => String(r.sol_id)}
                moduleId="open-orders"
                maxHeight="100%"
                columnWidths={{
                  customer: 100,
                  po_number: 110,
                  po_ln: 90,
                  so_number: 100,
                  line: 50,
                  customer_part_number: 120,
                  due_on: 100,
                  confirmation_date: 110,
                  last_revision: 110,
                  ordered: 80,
                  open: 80,
                  on_hand: 90,
                  replenish_on_order: 90,
                  state: 110,
                  odoo_last_updated: 110,
                  vendor_po: 110,
                  po_due: 100,
                  notes: 220,
                }}
                minTableWidth={2320}
                emptyState={
                  <EmptyState
                    variant="no-match"
                    title="No matches"
                    description="Try clearing a filter or broadening the date range."
                    action={
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setFilter(mode === 'history' ? historyFilter(minDate) : defaultFilter())
                        }
                      >
                        Reset
                      </Button>
                    }
                  />
                }
              />
            </div>
          ) : (
            <div className="flex-1 overflow-auto bg-canvas p-6">
              {isError ? (
                <QueryErrorState
                  error={error}
                  onRetry={() => void refetch()}
                  subject="open orders"
                />
              ) : mode === 'history' ? (
                <OrderHistorySummary
                  rows={filtered}
                  dateFrom={filter.date_from}
                  dateTo={filter.date_to}
                />
              ) : (
                <OpenOrdersSummary rows={filtered} />
              )}
            </div>
          )}

          {viewMode === 'table' ? (
            <footer className="flex items-center justify-between border-t border-border-subtle bg-surface-sunk px-3 py-1.5 text-2xs text-secondary">
              <span>
                Total ordered{' '}
                <strong className="text-primary">
                  {isError ? '—' : (data?.totals?.ordered ?? 0).toLocaleString()}
                </strong>{' '}
                · Total open{' '}
                <strong className="text-primary">
                  {isError ? '—' : (data?.totals?.open ?? 0).toLocaleString()}
                </strong>
              </span>
              <span>/ focus search · j/k navigate · Enter open · Esc close</span>
            </footer>
          ) : null}
        </div>
      </div>

      <OrderDrawer
        row={selectedRow}
        onClose={() => setSelectedRowId(undefined)}
        onPrev={drawerPrev}
        onNext={drawerNext}
        onViewCoverage={(productId) => {
          // Swap drawers: close OrderDrawer, open ProductCoverageDrawer.
          // Keeping only one drawer visible at a time — see drawer's own
          // docstring on the rationale.
          setSelectedRowId(undefined)
          setCoverageProductId(productId)
        }}
      />

      <ProductCoverageDrawer
        productId={coverageProductId}
        onClose={() => setCoverageProductId(null)}
      />

      <ExportDrawer
        open={exportOpen}
        onOpenChange={setExportOpen}
        rows={filtered}
        columns={ORDER_EXPORT_COLUMNS}
        moduleId="open-orders"
        filenameBase="spi-open-orders"
      />
    </div>
  )
}

function defaultLabelFor(filter: OpenOrdersFilter): string {
  const bits: string[] = []
  if (filter.rules?.overdue_only) bits.push('overdue')
  if (filter.rules?.outstanding_only) bits.push('outstanding')
  if (filter.exclusions?.exclude_samples) bits.push('no samples')
  return bits.length ? bits.join(' · ') : 'Filter ' + new Date().toLocaleDateString()
}

// Summary view — bar chart of state counts.
// Keeps the primitives consistent (no new chart library today).
function OpenOrdersSummary({ rows }: { rows: OpenOrderRow[] }) {
  const byState = rows.reduce(
    (acc, r) => {
      const key = `${r.status.toLowerCase()}-${r.coverage_status ?? 'none'}`
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )
  const total = rows.length || 1
  const entries = Object.entries(byState).sort((a, b) => b[1] - a[1])

  return (
    <div className="mx-auto max-w-[720px]">
      <h2 className="mb-3 text-lg font-semibold text-primary">State distribution</h2>
      <ul className="flex flex-col gap-1.5">
        {entries.map(([key, count]) => (
          <li key={key} className="flex items-center gap-3">
            <span className="w-48 text-xs uppercase tracking-widest text-tertiary">
              {key.replace('-', ' · ')}
            </span>
            <span
              className="h-4 rounded-xs bg-accent"
              style={{ width: `${(count / total) * 100}%`, minWidth: '2px' }}
            />
            <span className="text-xs text-secondary">{count}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
